import type { PropsWithChildren, ReactElement } from 'react';
import { Children, Component, Fragment, cloneElement } from 'react';

import { Divider, List } from '@material-ui/core'
import { type ListProps } from '@material-ui/core/List'
import { type ListItemProps } from '@material-ui/core/ListItem'
import { type PopoverOrigin } from '@material-ui/core/Popover'

import { type IMenuListItemProps } from './MenuListItem'
import { MenuListPopoverStyled } from './styles/MenuListPopoverStyled'
import { type IUIProviderValue, UIContext } from '../../core/UIContext'
import { TransparentIconButton } from '../../elements/Button'

export interface IMenuListProps extends ListProps {
  triggerElement?: ReactElement<any>
  closeOnSelect?: boolean
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  ariaLabel?: string
}

export class MenuList extends Component<
  PropsWithChildren<IMenuListProps>
> {
  static defaultProps = {
    open: false,
    closeOnSelect: true,
    anchorOrigin: { horizontal: 'right', vertical: 'top' },
    transformOrigin: { horizontal: 'right', vertical: 'top' },
  }
  static contextType = UIContext
  context: IUIProviderValue

  state = {
    anchorEl: null,
  }

  openMenu = (e) => {
    this.setState({ anchorEl: e.currentTarget })
  }

  handleClose = () => {
    this.setState({ anchorEl: null })
  }

  handleSelection = (callback) => (e) => {
    if (callback) {
      callback()
    }
    this.handleClose()
  }

  render() {
    const { anchorEl } = this.state
    const open = !!anchorEl

    const { container } = this.context

    let triggerElement = this.props.triggerElement || (
      <TransparentIconButton
        icon='more'
        type='button'
        aria-label={this.props.ariaLabel || 'Open menu list'}
      />
    )
    triggerElement = cloneElement(triggerElement, {
      onClick: this.openMenu,
      className: 'menu-list-trigger-element',
      'aria-label': this.props.ariaLabel || 'Open menu list',
    })

    const childrenUpdatedProp = Children.map(
      this.props.children as ReactElement<IMenuListItemProps>[],
      (child: ReactElement<IMenuListItemProps>) => {
        if (!child) return child

        if (child.props.closeOnSelect) {
          return cloneElement(child, {
            onClick: this.handleSelection(child.props.onClick),
          });
        } else if (
          (child.props.closeOnSelect === null ||
            child.props.closeOnSelect === undefined) &&
          this.props.closeOnSelect
        ) {
          return cloneElement(child, {
            onClick: this.handleSelection(child.props.onClick),
          });
        } else {
          return child
        }
      }
    )

    const stickyListItems: ReactElement<ListItemProps>[] = []
    const scrollableListItems: ReactElement<ListItemProps>[] = []

    Children.forEach(
      childrenUpdatedProp,
      (child: ReactElement<IMenuListItemProps>) => {
        if (child.props.sticky) {
          stickyListItems.push(child)
        } else {
          scrollableListItems.push(child)
        }
      }
    )

    return (
      <>
        {triggerElement}
        <MenuListPopoverStyled
          className='menu-list'
          open={open}
          anchorEl={anchorEl}
          onClose={this.handleClose}
          anchorOrigin={this.props.anchorOrigin}
          transformOrigin={this.props.transformOrigin}
          container={container}
        >
          <List
            className='menu-list-list'
            style={{
              maxHeight: '200px',
              overflowX: 'hidden',
              overflowY: 'auto',
              paddingTop: '0.75rem',
              paddingBottom: '0.75rem',
            }}
          >
            {scrollableListItems}
          </List>
          {stickyListItems.length > 0 && (
            <Fragment>
              <Divider />
              <List
                className='menu-list-sticky-list'
                style={{ overflow: 'hidden' }}
              >
                {stickyListItems}
              </List>
            </Fragment>
          )}
        </MenuListPopoverStyled>
      </>
    );
  }
}
